import { QueryParamName } from '../constants/urls/queryParams';
import { makeRedactionMarker } from '../security/redactionMarker';
import { isValidUUIDv4 } from '../validation/uuid';
import {
  entityIdsInObject,
  MemoryAssociableEntityIds,
} from './MemoryAssociableEntityIds';

export const MAX_CHAT_VIEW_PAGE_URL_LENGTH = 2048;

export enum ChatViewPageContentAccess {
  ALLOWED = 'ALLOWED',
  NOT_ALLOWED = 'NOT_ALLOWED',
}

export type ChatViewContext = {
  content?: string;
  contentHash?: string;
  entityIds: MemoryAssociableEntityIds;
  openEmailMessageId?: string;
  pageContentAccess?: ChatViewPageContentAccess;
  pageUrl?: string;
};

type QueryValue = string | string[] | null | undefined;

const CHAT_VIEW_QUERY_KEYS_IN_PAGE_URL_ORDER = [
  QueryParamName.QUOTE_ID,
  QueryParamName.ORDER_ID,
  QueryParamName.SHIPMENT_ID,
  QueryParamName.MOVEMENT_ID,
  QueryParamName.TRACK_MOVEMENT_ID,
  QueryParamName.TRACK_SELECTED_MOVEMENT_ID,
  QueryParamName.ORIGINATOR_ID,
] as const;

const ENTITY_QUERY_KEY_BY_CHAT_VIEW_PARAM: Record<string, string> = {
  [QueryParamName.QUOTE_ID]: QueryParamName.QUOTE_ID,
  [QueryParamName.ORDER_ID]: QueryParamName.ORDER_ID,
  [QueryParamName.SHIPMENT_ID]: QueryParamName.SHIPMENT_ID,
  [QueryParamName.MOVEMENT_ID]: QueryParamName.MOVEMENT_ID,
  [QueryParamName.TRACK_MOVEMENT_ID]: QueryParamName.MOVEMENT_ID,
  [QueryParamName.TRACK_SELECTED_MOVEMENT_ID]: QueryParamName.MOVEMENT_ID,
  [QueryParamName.ORIGINATOR_ID]: QueryParamName.ORIGINATOR_ID,
};

export function getChatViewContextFromLocation(
  pathname: string,
  query: Record<string, QueryValue>
): ChatViewContext {
  const pageSearch = new URLSearchParams();
  const entityQuery: Record<string, string[]> = {};

  for (const queryKey of CHAT_VIEW_QUERY_KEYS_IN_PAGE_URL_ORDER) {
    const validIds = getValidQueryUuids(query[queryKey]);
    if (validIds.length === 0) continue;
    for (const id of validIds) {
      pageSearch.append(queryKey, id);
    }
    const entityKey = ENTITY_QUERY_KEY_BY_CHAT_VIEW_PARAM[queryKey];
    entityQuery[entityKey] = [...(entityQuery[entityKey] ?? []), ...validIds];
  }

  const normalizedPathname = pathname.startsWith('/')
    ? pathname
    : `/${pathname}`;
  const search = pageSearch.toString();
  return {
    entityIds: entityIdsInObject(entityQuery),
    pageUrl: search ? `${normalizedPathname}?${search}` : normalizedPathname,
  };
}

export function prepareChatPageUrlForContext(
  pageUrl?: string | null
): string | undefined {
  if (!pageUrl) return undefined;
  const trimmed = pageUrl.trim();
  if (trimmed.length > MAX_CHAT_VIEW_PAGE_URL_LENGTH) return undefined;

  try {
    const url = new URL(trimmed);
    if (!url.username && !url.password) return trimmed;
    const redactedUsername = url.username
      ? makeRedactionMarker(decodeUrlCredential(url.username), 'username')
      : '';
    const redactedPassword = url.password
      ? makeRedactionMarker(decodeUrlCredential(url.password), 'password')
      : '';
    const redactedCredentials = redactedPassword
      ? `${redactedUsername}:${redactedPassword}`
      : redactedUsername;
    url.username = '';
    url.password = '';
    const credentiallessUrl = url.toString();
    const authorityStart = credentiallessUrl.indexOf('//') + 2;
    return `${credentiallessUrl.slice(
      0,
      authorityStart
    )}${redactedCredentials}@${credentiallessUrl.slice(authorityStart)}`;
  } catch {
    return trimmed;
  }
}

function decodeUrlCredential(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function getValidQueryUuids(value: QueryValue): string[] {
  const values = Array.isArray(value) ? value : [value];
  return values.filter(
    (id): id is string => typeof id === 'string' && isValidUUIDv4(id)
  );
}
